// Minimal vector / quaternion maths for the simulation. Deliberately free of
// Three.js so the sim can run headless (tests, workers). Conventions match
// Three.js: right-handed, +Y up. Ship-local frame: +X forward (bow), +Y up,
// +Z starboard. World compass: north = -Z, east = +X.

export const TAU = Math.PI * 2;

export function clamp(v: number, lo: number, hi: number): number {
  return v < lo ? lo : v > hi ? hi : v;
}

export function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

export function smoothstep(e0: number, e1: number, x: number): number {
  const t = clamp((x - e0) / (e1 - e0), 0, 1);
  return t * t * (3 - 2 * t);
}

/** Frame-rate independent exponential approach of `a` toward `b`. */
export function damp(a: number, b: number, rate: number, dt: number): number {
  return b + (a - b) * Math.exp(-rate * dt);
}

/** Wrap an angle to (-PI, PI]. */
export function wrapAngle(a: number): number {
  a = (a + Math.PI) % TAU;
  if (a < 0) a += TAU;
  return a - Math.PI;
}

export function dampAngle(a: number, b: number, rate: number, dt: number): number {
  return a + wrapAngle(b - a) * (1 - Math.exp(-rate * dt));
}

/** Compass bearing (radians, clockwise from north) to a horizontal unit vector. */
export function bearingToX(b: number): number {
  return Math.sin(b);
}
export function bearingToZ(b: number): number {
  return -Math.cos(b);
}
export function vecToBearing(x: number, z: number): number {
  const b = Math.atan2(x, -z);
  return b < 0 ? b + TAU : b;
}

export class Vec3 {
  constructor(public x = 0, public y = 0, public z = 0) {}

  set(x: number, y: number, z: number): this {
    this.x = x;
    this.y = y;
    this.z = z;
    return this;
  }
  copy(v: Vec3): this {
    this.x = v.x;
    this.y = v.y;
    this.z = v.z;
    return this;
  }
  clone(): Vec3 {
    return new Vec3(this.x, this.y, this.z);
  }
  add(v: Vec3): this {
    this.x += v.x;
    this.y += v.y;
    this.z += v.z;
    return this;
  }
  sub(v: Vec3): this {
    this.x -= v.x;
    this.y -= v.y;
    this.z -= v.z;
    return this;
  }
  scale(s: number): this {
    this.x *= s;
    this.y *= s;
    this.z *= s;
    return this;
  }
  addScaled(v: Vec3, s: number): this {
    this.x += v.x * s;
    this.y += v.y * s;
    this.z += v.z * s;
    return this;
  }
  dot(v: Vec3): number {
    return this.x * v.x + this.y * v.y + this.z * v.z;
  }
  /** this = a x b */
  crossVectors(a: Vec3, b: Vec3): this {
    const x = a.y * b.z - a.z * b.y;
    const y = a.z * b.x - a.x * b.z;
    const z = a.x * b.y - a.y * b.x;
    this.x = x;
    this.y = y;
    this.z = z;
    return this;
  }
  length(): number {
    return Math.sqrt(this.x * this.x + this.y * this.y + this.z * this.z);
  }
  lengthSq(): number {
    return this.x * this.x + this.y * this.y + this.z * this.z;
  }
  normalize(): this {
    const l = this.length();
    if (l > 1e-12) this.scale(1 / l);
    return this;
  }
  lerp(v: Vec3, t: number): this {
    this.x += (v.x - this.x) * t;
    this.y += (v.y - this.y) * t;
    this.z += (v.z - this.z) * t;
    return this;
  }
  distanceTo(v: Vec3): number {
    const dx = this.x - v.x;
    const dy = this.y - v.y;
    const dz = this.z - v.z;
    return Math.sqrt(dx * dx + dy * dy + dz * dz);
  }
}

export class Quat {
  constructor(public x = 0, public y = 0, public z = 0, public w = 1) {}

  set(x: number, y: number, z: number, w: number): this {
    this.x = x;
    this.y = y;
    this.z = z;
    this.w = w;
    return this;
  }
  copy(q: Quat): this {
    return this.set(q.x, q.y, q.z, q.w);
  }
  clone(): Quat {
    return new Quat(this.x, this.y, this.z, this.w);
  }
  setFromAxisAngle(ax: number, ay: number, az: number, angle: number): this {
    const h = angle / 2;
    const s = Math.sin(h);
    return this.set(ax * s, ay * s, az * s, Math.cos(h));
  }
  /** Yaw about +Y, then pitch about local +Z, then roll about local +X. */
  setFromYawPitchRoll(yaw: number, pitch: number, roll: number): this {
    const qy = new Quat().setFromAxisAngle(0, 1, 0, yaw);
    const qp = new Quat().setFromAxisAngle(0, 0, 1, pitch);
    const qr = new Quat().setFromAxisAngle(1, 0, 0, roll);
    return this.copy(qy).multiply(qp).multiply(qr);
  }
  /** this = this * q */
  multiply(q: Quat): this {
    const ax = this.x, ay = this.y, az = this.z, aw = this.w;
    const bx = q.x, by = q.y, bz = q.z, bw = q.w;
    this.x = ax * bw + aw * bx + ay * bz - az * by;
    this.y = ay * bw + aw * by + az * bx - ax * bz;
    this.z = az * bw + aw * bz + ax * by - ay * bx;
    this.w = aw * bw - ax * bx - ay * by - az * bz;
    return this;
  }
  normalize(): this {
    const l = Math.hypot(this.x, this.y, this.z, this.w) || 1;
    this.x /= l;
    this.y /= l;
    this.z /= l;
    this.w /= l;
    return this;
  }
  /** Rotate v in place by this quaternion. */
  rotate(v: Vec3): Vec3 {
    const { x, y, z, w } = this;
    const ix = w * v.x + y * v.z - z * v.y;
    const iy = w * v.y + z * v.x - x * v.z;
    const iz = w * v.z + x * v.y - y * v.x;
    const iw = -x * v.x - y * v.y - z * v.z;
    v.x = ix * w + iw * -x + iy * -z - iz * -y;
    v.y = iy * w + iw * -y + iz * -x - ix * -z;
    v.z = iz * w + iw * -z + ix * -y - iy * -x;
    return v;
  }
  /** Rotate v in place by the inverse of this quaternion. */
  rotateInverse(v: Vec3): Vec3 {
    this.x = -this.x;
    this.y = -this.y;
    this.z = -this.z;
    this.rotate(v);
    this.x = -this.x;
    this.y = -this.y;
    this.z = -this.z;
    return v;
  }
  /** Integrate a world-space angular velocity over dt. */
  integrate(wx: number, wy: number, wz: number, dt: number): this {
    const hx = wx * dt * 0.5, hy = wy * dt * 0.5, hz = wz * dt * 0.5;
    const { x, y, z, w } = this;
    this.x += hx * w + hy * z - hz * y;
    this.y += hy * w + hz * x - hx * z;
    this.z += hz * w + hx * y - hy * x;
    this.w += -hx * x - hy * y - hz * z;
    return this.normalize();
  }
  slerp(q: Quat, t: number): this {
    let { x, y, z, w } = q;
    let cos = this.x * x + this.y * y + this.z * z + this.w * w;
    if (cos < 0) {
      cos = -cos;
      x = -x;
      y = -y;
      z = -z;
      w = -w;
    }
    if (cos > 0.9995) {
      this.x += (x - this.x) * t;
      this.y += (y - this.y) * t;
      this.z += (z - this.z) * t;
      this.w += (w - this.w) * t;
      return this.normalize();
    }
    const theta = Math.acos(cos);
    const sin = Math.sin(theta);
    const a = Math.sin((1 - t) * theta) / sin;
    const b = Math.sin(t * theta) / sin;
    this.x = this.x * a + x * b;
    this.y = this.y * a + y * b;
    this.z = this.z * a + z * b;
    this.w = this.w * a + w * b;
    return this;
  }
}
